import { useCallback, useEffect, useRef, useState } from 'react';
import {
    type ProductSyncRun,
    type TriggerResult,
    type RejectedProduct,
    type StockSyncRun,
    type SyncStore,
    fetchProductSyncRuns,
    fetchRejectedProducts,
    fetchStockSyncRuns,
    fetchSyncStores,
    describeTriggerResult,
    triggerProductReconcile,
    triggerProductSync,
    triggerStockSync,
} from '../services/syncService';
import { getErrorMessage } from '../utils/errors';

/**
 * Polls while anything is running.
 *
 * A sync can take minutes, so the UI never waits on the invoke promise — it starts
 * the run, then watches the run table, which is the same thing the scheduled job
 * relies on. `notice` carries the outcomes that are not failures but are not "done"
 * either: BUSY when the cron job got there first, SKIPPED for an unmapped store.
 */
const POLL_MS = 3000;

export function useStockSyncRuns() {
    const [runs, setRuns] = useState<StockSyncRun[]>([]);
    const [stores, setStores] = useState<SyncStore[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    /** The store whose button was pressed, so only that row shows a busy state. */
    const [triggeringStoreId, setTriggeringStoreId] = useState<string | null>(null);

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Both together: the page lists every store, including ones that have
            // never synced, so a store nobody has mapped to Arch is visible rather
            // than simply absent.
            const [runData, storeData] = await Promise.all([
                fetchStockSyncRuns(),
                fetchSyncStores(),
            ]);
            setRuns(runData);
            setStores(storeData);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load stock sync runs'));
        } finally {
            setLoading(false);
        }
    }, []);

    const anyRunning = runs.some((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING');

    // Keep the latest fetcher in a ref so the interval below does not need to be
    // torn down and rebuilt every render.
    const fetchRef = useRef(fetchRuns);
    fetchRef.current = fetchRuns;

    useEffect(() => {
        if (!anyRunning && !triggeringStoreId) return;
        const id = setInterval(() => void fetchRef.current(), POLL_MS);
        return () => clearInterval(id);
    }, [anyRunning, triggeringStoreId]);

    const runSync = useCallback(async (storeId: string) => {
        setTriggeringStoreId(storeId);
        setNotice(null);
        setError(null);
        try {
            const result = await triggerStockSync(storeId);
            setNotice(describeTriggerResult(result));
            return result;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to start the sync'));
            return null;
        } finally {
            setTriggeringStoreId(null);
            // The run row is the record, not the response — re-read it either way.
            await fetchRef.current();
        }
    }, []);

    return {
        runs, stores, loading, error, notice, triggeringStoreId,
        fetchRuns, runSync, dismissNotice: () => setNotice(null),
    };
}

export function useProductSyncRuns() {
    const [runs, setRuns] = useState<ProductSyncRun[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [triggering, setTriggering] = useState<'sync' | 'reconcile' | null>(null);

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRuns(await fetchProductSyncRuns());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load product sync runs'));
        } finally {
            setLoading(false);
        }
    }, []);

    const anyRunning = runs.some((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING');

    const fetchRef = useRef(fetchRuns);
    fetchRef.current = fetchRuns;

    useEffect(() => {
        if (!anyRunning && !triggering) return;
        const id = setInterval(() => void fetchRef.current(), POLL_MS);
        return () => clearInterval(id);
    }, [anyRunning, triggering]);

    const run = useCallback(async (which: 'sync' | 'reconcile') => {
        setTriggering(which);
        setNotice(null);
        setError(null);
        try {
            const trigger = which === 'sync' ? triggerProductSync : triggerProductReconcile;
            const result: TriggerResult = await trigger();
            setNotice(describeTriggerResult(result));
            return result;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to start the run'));
            return null;
        } finally {
            setTriggering(null);
            await fetchRef.current();
        }
    }, []);

    return { runs, loading, error, notice, triggering, fetchRuns, run, dismissNotice: () => setNotice(null) };
}

export function useRejectedProducts() {
    const [rejections, setRejections] = useState<RejectedProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRejections = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRejections(await fetchRejectedProducts());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load rejected products'));
        } finally {
            setLoading(false);
        }
    }, []);

    return { rejections, loading, error, fetchRejections };
}
